import type { DeepReadonly } from "ts-essentials";

import type {
  MatchFull,
  MatchStart,
  PlayerMatchRoundStat,
  RoundEnd,
} from "@/data-models/csgo-game-events.mjs";
import orderArrayBy from "@/util/order-array-by.mjs";

export const PLACEMENT = {
  DRAW: 0,
  VICTORY: 1,
  DEFEAT: 2,
};

export const TEAM_IDS = {
  // SPECTATOR: 0,
  T: 2,
  CT: 3,
};

export const getLastRound = (match: DeepReadonly<MatchFull | MatchStart>) => {
  const rounds =
    Array.isArray(match?.rounds) && match.rounds.length && match.rounds.slice();
  if (!rounds) return;
  return orderArrayBy(rounds, "roundNumber", "desc").find(
    (i) =>
      Array.isArray(i.playerMatchRoundStats) && i.playerMatchRoundStats.length,
  );
};

export const getRoundsLength = (
  rounds: DeepReadonly<MatchFull>["rounds"] = [],
) => {
  let max = 0;
  try {
    for (const round of rounds) {
      if (typeof round?.roundNumber !== "number" || round.roundNumber <= max)
        continue;
      max = round.roundNumber;
    }
  } catch {
    // Silently handle errors
  }
  return max > 0 ? max + 1 : max;
};

export const getWonLost = ({
  player,
  round,
}: {
  player?: PlayerMatchRoundStat;
  round?: DeepReadonly<RoundEnd>;
}) => {
  if (!player || !round) return { won: 0, lost: 0 };
  const tScore = round.terroristCurrentScore;
  const ctScore = round.counterTerroristCurrentScore;
  return player.teamId === TEAM_IDS.T
    ? { won: tScore, lost: ctScore }
    : { won: ctScore, lost: tScore };
};

export const getPlayerPlacement = ({
  wonLost: { won, lost },
  match,
  rank,
}: {
  wonLost: { won: number; lost: number };
  match: DeepReadonly<MatchFull | MatchStart>;
  rank: number;
}) => {
  if (match?.gameType === 0) {
    return won === lost
      ? PLACEMENT.DRAW
      : won > lost
        ? PLACEMENT.VICTORY
        : PLACEMENT.DEFEAT;
  }
  return rank;
};

export const getMatchResultLabel = (
  placement: number,
  match: MatchStart | MatchFull,
) => {
  if (match?.gameType === 0) {
    const result = {
      label: "",
      color: undefined,
      win: false,
      loss: true,
      draw: false,
    };
    switch (placement) {
      case PLACEMENT.DRAW:
        result.label = "Draw";
        result.color = "var(--shade3)";
        result.draw = true;
        break;
      case PLACEMENT.VICTORY:
        result.label = "Victory";
        result.color = "var(--turq)";
        result.win = true;
        break;
      case PLACEMENT.DEFEAT:
        result.label = "Defeat";
        result.color = "var(--red)";
        break;
      default:
        result.label = "DNF";
    }
    return result;
  }
  return {
    label: placement > 0 ? `${placement}` : "DNF",
    color: undefined,
    win: placement > 0 && placement <= 3,
    loss: false,
    draw: false,
  };
};

export const isDeathMatch = (match: DeepReadonly<MatchFull>) => {
  return match?.gameType === 1 && match?.gameMode === 2;
};
